import { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import ReactTimeout from 'react-timeout';
import { noop } from 'helpers/GlobalHelper';
import handleShouldComponentUpdate from 'helpers/Lifecycle';
import { isSameSecond, MILLISECONDS } from 'helpers/DateTimeHelper';
import { fetchNextPanelPage, fetchPanel, setFirstPage, toggleFetching } from './PanelActions';

class Panel extends Component {
  constructor(props) {
    super(props);

    this.state = {
      timer: null,
    };

    this.validForTimedOut = this.validForTimedOut.bind(this);
  }

  componentDidMount() {
    this.initialFetch(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      accessToken,
      calculateValidFor,
      panel: nextPanel,
      panelData: nextPanelData,
      panelUrl: nextPanelUrl,
      shouldFetchMore: nextShouldFetchMore,
    } = nextProps;
    const {
      accessToken: prevAccessToken,
      deviceId,
      fetchNextPanelPage: actionFetchNextPanelPage,
      hasInfiniteScroll,
      hasMoreItems,
      headerGenerator,
      pageType,
      panel,
      panelData,
      panelId,
      panelUrl,
      routeGenerator,
      setTimeout: actionSetTimeout,
      shouldFetchMore,
      toggleFetching: actionToggleFetching,
    } = this.props;
    const { timer } = this.state;

    const newPanelUrl = nextPanelUrl !== panelUrl;
    const newPanelData = JSON.stringify(nextPanelData) !== JSON.stringify(panelData);
    const nextPanelPaginationUrl = _.get(nextPanel, 'paginationUrl');
    const panelPaginationUrl = _.get(panel, 'paginationUrl');
    const paginationUrlChanged = nextPanelPaginationUrl !== panelPaginationUrl;
    const shouldFetchMoreChanged = nextShouldFetchMore !== shouldFetchMore;

    if (shouldFetchMoreChanged) {
      const url = _.get(panel, 'panelData.items.view.next');
      const headers = headerGenerator({ pathKey: pageType, accessToken, deviceId });
      actionToggleFetching(true);
      actionFetchNextPanelPage(url, headers, panelId);
    }

    if (nextPanelPaginationUrl && paginationUrlChanged && !hasInfiniteScroll && hasMoreItems) {
      const headers = headerGenerator({ pathKey: pageType, accessToken, deviceId });
      const url = routeGenerator(nextPanel.paginationUrl);
      actionToggleFetching(true);
      actionFetchNextPanelPage(url, headers, panelId);
    }

    const accessTokenChanged = prevAccessToken !== accessToken;
    const dataChanged = newPanelUrl || newPanelData;
    if (dataChanged || accessTokenChanged) this.initialFetch(nextProps);

    if (calculateValidFor && !timer) {
      const nextValidFor = calculateValidFor(this.getPanelData(nextProps));
      if (nextValidFor) {
        this.clearTimer();
        const newTimer = actionSetTimeout(this.validForTimedOut, nextValidFor * MILLISECONDS);
        this.setState({ timer: newTimer });
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { breakpoint, propsToWatch, panelPropsToWatch } = this.props;
    const {
      breakpoint: nextBreakpoint,
      propsToWatch: nextPropsToWatch,
      panelPropsToWatch: nextPanelPropsToWatch,
    } = nextProps;

    const lastChange = this.getPanelLastUpdated(this.props);
    const nextChange = this.getPanelLastUpdated(nextProps);
    const isSame = isSameSecond(lastChange, nextChange);

    if (lastChange && !isSame) return true;

    // given a unique id and the location of the item in props
    // allow parent to send in custom prop locations to check for comparison
    // EX: use fetching to trigger component update during scrolling in header search results
    const panelPropsToCompare = panelPropsToWatch.reduce((acc, prop) => {
      const { uid, location } = prop;
      const value = _.get(this.props, location);
      if (uid && value) return { ...acc, [uid]: value };
      return acc;
    }, {});
    const nextPanelPropsToCompare = nextPanelPropsToWatch.reduce((acc, prop) => {
      const { uid, location } = prop;
      const value = _.get(nextProps, location);
      if (uid && value) return { ...acc, [uid]: value };
      return acc;
    }, {});

    const options = {
      props: {
        ...this.getPanelData(this.props),
        breakpoint,
        ...propsToWatch,
        ...panelPropsToCompare,
      },
      state: this.state,
      nextProps: {
        ...this.getPanelData(nextProps),
        breakpoint: nextBreakpoint,
        ...nextPropsToWatch,
        ...nextPanelPropsToCompare,
      },
      nextState,
    };

    return handleShouldComponentUpdate(options);
  }

  getPanelLastUpdated = props => _.get(props, 'panel.lastUpdated');

  getPanelData = props => _.get(props, 'panel.panelData', _.get(props, 'panelData'));

  validForTimedOut() {
    const {
      accessToken,
      calculateValidFor,
      headerGenerator,
      fetchPanel: actionFetchPanel,
      routeGenerator,
      panelId,
      panelUrl,
      pageType,
    } = this.props;
    const nextValidFor = calculateValidFor(this.getPanelData(this.props));
    if (nextValidFor <= 0) {
      this.clearTimer();
      const { '@id': id } = this.getPanelData(this.props);
      const url = panelUrl ? routeGenerator(panelUrl) : routeGenerator(id);
      const headers = headerGenerator({ pathKey: pageType, accessToken });
      actionFetchPanel(url, headers, panelId);
    }
  }

  clearTimer() {
    const { timer } = this.state;
    const { clearTimeout: actionClearTimeout } = this.props;
    if (!timer) return;
    actionClearTimeout(timer);
    this.setState({ timer: null });
  }

  initialFetch(props) {
    const {
      accessToken,
      deviceId,
      fetchPanel: actionFetchPanel,
      headerGenerator,
      onRef,
      pageType,
      panelData,
      panelId,
      panelUrl,
      routeGenerator,
      setFirstPage: actionSetFirstPage,
    } = props;
    if (onRef) onRef(this);

    const hasItems = _.get(panelData, 'items', false);
    const hasMembers = _.get(panelData, 'member', false);
    const hasTypes = _.get(panelData, 'types', false);
    const shouldFetch = !hasItems && !hasMembers && !hasTypes;
    if (shouldFetch) {
      const { '@id': id } = panelData;
      const url = panelUrl ? routeGenerator(panelUrl) : routeGenerator(id);
      const headers = headerGenerator({ pathKey: pageType, accessToken, deviceId });
      actionFetchPanel(url, headers, panelId);
    }

    actionSetFirstPage(panelData, panelId);
  }

  fetchNextPage(v2) {
    const {
      accessToken,
      deviceId,
      fetching,
      fetchNextPanelPage: actionFetchNextPanelPage,
      headerGenerator,
      pageType,
      panel,
      panelId,
      routeGenerator,
      toggleFetching: actionToggleFetching,
    } = this.props;
    if ((!panel || !panel.hasMoreItems || fetching) && !v2) return;
    const headers = headerGenerator({ pathKey: pageType, accessToken, deviceId });
    const url = routeGenerator(panel.paginationUrl);
    actionToggleFetching(true);
    actionFetchNextPanelPage(url, headers, panelId);
  }

  isFetching() {
    const { fetching: actionFetching } = this.prop;
    return actionFetching;
  }

  render() {
    const { renderer } = this.props;
    const panelData = this.getPanelData(this.props);
    if (!panelData) return null;
    return renderer(panelData);
  }
}

function mapStateToProps(state, ownProps) {
  const panelId = _.get(ownProps, 'panelId');
  const panels = _.get(state, 'panel.panels');
  const panel = _.get(panels, panelId);
  return {
    panel,
    fetching: state.panel.fetching,
  };
}

Panel.defaultProps = {
  accessToken: null,
  breakpoint: null,
  calculateValidFor: noop,
  deviceId: null,
  fetching: false,
  hasInfiniteScroll: false,
  hasMoreItems: false,
  headerGenerator: noop,
  panel: null,
  panelPropsToWatch: [],
  panelUrl: null,
  propsToWatch: {},
  routeGenerator: noop,
  shouldFetchMore: false,
};

Panel.propTypes = {
  accessToken: PropTypes.string,
  breakpoint: PropTypes.string,
  calculateValidFor: PropTypes.func,
  clearTimeout: PropTypes.func.isRequired,
  deviceId: PropTypes.string,
  fetching: PropTypes.bool,
  fetchNextPanelPage: PropTypes.func.isRequired,
  fetchPanel: PropTypes.func.isRequired,
  hasInfiniteScroll: PropTypes.bool,
  hasMoreItems: PropTypes.bool,
  headerGenerator: PropTypes.func,
  pageType: PropTypes.string.isRequired,
  panel: PropTypes.shape({}),
  panelData: PropTypes.shape({}).isRequired,
  panelId: PropTypes.string.isRequired,
  panelPropsToWatch: PropTypes.arrayOf(PropTypes.shape({})),
  panelUrl: PropTypes.string,
  propsToWatch: PropTypes.shape({}),
  renderer: PropTypes.func.isRequired,
  routeGenerator: PropTypes.func,
  setTimeout: PropTypes.func.isRequired,
  shouldFetchMore: PropTypes.bool,
  toggleFetching: PropTypes.func.isRequired,
};

const TimeoutPanel = ReactTimeout(
  connect(mapStateToProps, {
    fetchNextPanelPage,
    fetchPanel,
    setFirstPage,
    toggleFetching,
  })(Panel),
);

export default TimeoutPanel;
